import {
  createBinnedDataBufferFromData,
  GeckoBinnedDataBuffer,
} from 'features/dataLayers/geckoBinnedDataBuffer'
import { memoize, MemoizedFunction } from 'lodash'
import { GeckoComponentDataFrame, RolodexEntryType } from 'types'

const SCHEMA_VERSION = 13

export type RawCdfEntry = {
  id: string
  type:
    | RolodexEntryType.COMPONENT_DATA_FRAME
    | RolodexEntryType.FDE_COMPONENT_DATA_FRAME
  index: Record<string, never>
  data: {
    params: {
      view: string
      source: string
      aggregation: string
      measurement_unit: string
      angle_plot_setting: number
      algorithm: string
      tmin_method?: string
    }
    binning: {
      x_offset: number
      y_offset: number
      bin_width: number
      bin_height: number
      x_bin_count: number
      y_bin_count: number
    }
    geometry: {
      x_end: number
      y_end: number
      x_start: number
      y_start: number
      x_measurement_unit: string
      y_measurement_unit: string
    }
    schema_version: number
  }
  generation: number
  updated_at: string
  tags: Record<string, string | null>
  view_restriction: null
}

export type CreateCdfParams = {
  view: GeckoComponentDataFrame['data']['params']['view']
  source: GeckoComponentDataFrame['data']['params']['source']
  aggregation: GeckoComponentDataFrame['data']['params']['aggregation']
  measurementUnit: GeckoComponentDataFrame['data']['params']['measurement_unit']
  anglePlotSetting: GeckoComponentDataFrame['data']['params']['angle_plot_setting']
  algorithm: GeckoComponentDataFrame['data']['params']['algorithm']
  tminMethod?: GeckoComponentDataFrame['data']['params']['tmin_method']
  binning: GeckoComponentDataFrame['data']['binning']
  geometry: GeckoComponentDataFrame['data']['geometry']
  tags: GeckoComponentDataFrame['tags']
  // you can either pass in the data itself, or a function that returns the data
  data:
    | ReadonlyArray<ReadonlyArray<number>>
    | (() => Promise<ReadonlyArray<ReadonlyArray<number>>>)
  generation?: number //optional param to set a generation number
  smoothing?: boolean
}

// Make a specific field in the type required
// this is useful for ensuring that the `generation` field should be specified
// when it gets to this function, but allows it to be added to the variable
// `args` in the `useCreateCustomCdfs` function so that it can be easily iterated.
type RequireField<T, K extends keyof T> = Omit<T, K> & Required<Pick<T, K>>

export function createCdfEntry(
  id: string,
  args: Omit<RequireField<CreateCdfParams, 'generation'>, 'data'>,
  type: RawCdfEntry['type'] = RolodexEntryType.COMPONENT_DATA_FRAME
): RawCdfEntry {
  return {
    id: id,
    type: type,
    index: {},
    data: {
      params: {
        view: args.view.id,
        source: args.source.id,
        aggregation: args.aggregation.id,
        measurement_unit: args.measurementUnit.id,
        algorithm: args.algorithm.id,
        angle_plot_setting: args.anglePlotSetting.id,
        tmin_method: args.tminMethod?.id,
      },
      binning: {
        x_offset: args.binning.offset.x.toNumber(args.geometry.x.unit),
        y_offset: args.binning.offset.y.toNumber(args.geometry.y.unit),
        bin_width: args.binning.bin_size.width.toNumber(args.geometry.x.unit),
        bin_height: args.binning.bin_size.height.toNumber(args.geometry.y.unit),
        x_bin_count: args.binning.x_bin_count,
        y_bin_count: args.binning.y_bin_count,
      },
      geometry: {
        x_end: args.geometry.end.x.toNumber(args.geometry.x.unit),
        y_end: args.geometry.end.y.toNumber(args.geometry.y.unit),
        x_start: args.geometry.start.x.toNumber(args.geometry.x.unit),
        y_start: args.geometry.start.y.toNumber(args.geometry.y.unit),
        x_measurement_unit: args.geometry.x.unit.id,
        y_measurement_unit: args.geometry.y.unit.id,
      },
      schema_version: SCHEMA_VERSION,
    },
    generation: args.generation,
    updated_at: new Date().toISOString(),
    tags: args.tags,
    view_restriction: null,
  }
}

export function createCdf(
  id: string,
  args: RequireField<CreateCdfParams, 'generation'>,
  type: RawCdfEntry['type'] = RolodexEntryType.COMPONENT_DATA_FRAME
): {
  entry: RawCdfEntry
  getBinnedDataBufferFn: (() => Promise<GeckoBinnedDataBuffer>) &
    MemoizedFunction
} | null {
  const entry: RawCdfEntry = createCdfEntry(id, args, type)

  const memoizedGetBinnedDataBufferFn = memoize(async () => {
    const data = (
      typeof args.data === 'function' ? await args.data() : args.data
    ).flat()

    return createBinnedDataBufferFromData(
      new Float32Array(data),
      GeckoComponentDataFrame.CreateFromJSON(entry)
    )
  })

  return { entry, getBinnedDataBufferFn: memoizedGetBinnedDataBufferFn }
}
